import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AppLayout } from "@/components/AppLayout";
import { motion } from "framer-motion";
import { Upload, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProcessInvoice = ({ user }) => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState('');
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/configs`, { withCredentials: true }).then(r => setConfigs(r.data)).catch(() => toast.error('Failed to load configurations'));
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf' && !selectedFile.type.startsWith('image/')) {
        toast.error('Please select a PDF or image file'); return;
      }
      setFile(selectedFile); setResult(null);
    }
  };

  const convertToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedConfig) { toast.error('Select a configuration'); return; }
    if (!file) { toast.error('Select a file'); return; }
    setProcessing(true); setResult(null);
    try {
      const base64String = await convertToBase64(file);
      const response = await axios.post(`${BACKEND_URL}/api/process-invoice`, {
        config_id: selectedConfig, file_id: `file_${Date.now()}`, file_name: file.name, image_base64: base64String, is_pdf: file.type === 'application/pdf'
      }, { withCredentials: true });
      setResult(response.data);
      if (response.data.warning) toast.warning(response.data.warning);
      else toast.success('Invoice processed successfully!');
      setFile(null); setSelectedConfig('');
      const fi = document.getElementById('file-upload');
      if (fi) fi.value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process invoice');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <AppLayout user={user} activeNav="/process">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
        <Card className="mb-6 bg-white/90 backdrop-blur-sm border border-stone-200/60">
          <CardHeader>
            <CardTitle className="font-heading">Process Invoice</CardTitle>
            <CardDescription>Upload an invoice or receipt to extract data automatically</CardDescription>
          </CardHeader>
          <CardContent>
            {configs.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-stone-500 mb-4">No configurations found</p>
                <Button onClick={() => navigate('/configurations')} className="bg-sage-500 hover:bg-sage-600 rounded-xl">Set Up Configuration First</Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label>Select Configuration</Label>
                  <Select value={selectedConfig} onValueChange={setSelectedConfig}>
                    <SelectTrigger data-testid="config-select" className="border-2 border-stone-200 rounded-xl"><SelectValue placeholder="Choose a folder configuration" /></SelectTrigger>
                    <SelectContent>{configs.map(c => <SelectItem key={c.config_id} value={c.config_id}>{c.drive_folder_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Upload Invoice/Receipt</Label>
                  <label htmlFor="file-upload" className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-stone-200 border-dashed rounded-xl cursor-pointer hover:border-sage-400 transition-colors">
                    <div className="space-y-1 text-center">
                      {file ? (
                        <>
                          <CheckCircle className="mx-auto h-10 w-10 text-green-500" />
                          <p className="text-sm text-stone-600">{file.name}</p>
                        </>
                      ) : (
                        <>
                          <Upload className="mx-auto h-10 w-10 text-stone-400" />
                          <p className="text-sm text-stone-600"><span className="font-medium text-sage-600">Click to upload</span></p>
                          <p className="text-xs text-stone-400">PDF or Image</p>
                        </>
                      )}
                    </div>
                  </label>
                  <input id="file-upload" type="file" data-testid="file-upload" accept="application/pdf,image/*" onChange={handleFileChange} className="sr-only" />
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => navigate('/dashboard')} className="rounded-xl">Cancel</Button>
                  <Button type="submit" disabled={processing || !file || !selectedConfig} data-testid="process-button" className="bg-sage-500 hover:bg-sage-600 rounded-xl">
                    {processing ? 'Processing...' : 'Process Invoice'}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {result && (
          <Card className={`border-2 ${result.sheet_updated ? 'border-green-200 bg-green-50/50' : 'border-amber-200 bg-amber-50/50'} backdrop-blur-sm`}>
            <CardHeader>
              <CardTitle className={`font-heading ${result.sheet_updated ? 'text-green-800' : 'text-amber-800'}`}>Processing Complete</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-white rounded-xl p-4 grid grid-cols-2 gap-3">
                <div><p className="text-[10px] font-bold text-stone-400 uppercase">Date</p><p className="text-sm text-stone-700">{result.extracted_data.date || '-'}</p></div>
                <div><p className="text-[10px] font-bold text-stone-400 uppercase">Vendor</p><p className="text-sm text-stone-700">{result.extracted_data.vendor_name || '-'}</p></div>
                <div><p className="text-[10px] font-bold text-stone-400 uppercase">Category</p><p className="text-sm text-stone-700">{result.extracted_data.category || '-'}</p></div>
                <div><p className="text-[10px] font-bold text-stone-400 uppercase">Amount</p><p className="text-sm font-bold text-stone-700">{result.extracted_data.amount ? `₹${result.extracted_data.amount}` : '-'}</p></div>
                <div className="col-span-2"><p className="text-[10px] font-bold text-stone-400 uppercase">Description</p><p className="text-sm text-stone-700">{result.extracted_data.description || '-'}</p></div>
              </div>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </AppLayout>
  );
};

export default ProcessInvoice;
